/**
 * Routing — the platform edition.
 *
 * Four territories now:
 *   /            public home (plans + register CTA), or a role redirect for a
 *                signed-in visitor with a selected club
 *   /sa/*        the Super Admin console
 *   /p/:id/*     an Admin Portal's own screens (owner or SA)
 *   /m/* /a/*    the CLUB portals — unchanged screens, per selected club;
 *                /m/events/:id and /a/events/:id still share EventDetail.
 *
 * Public link screens (/pay, /join, /invite, /forgot, /reset) render before
 * anything loads — they resolve their own tokens and touch no club state.
 */

import { Link, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useClub } from './context/ClubContext';
import { AppShell } from './components/AppShell';
import { SignIn } from './pages/SignIn';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { ResetPasswordPage } from './pages/ResetPassword';
import { EventDetailPage } from './pages/EventDetail';
import { PayPage } from './pages/Pay';
import { JoinPage } from './pages/Join';
import { InvitePage } from './pages/Invite';
import { HomePage } from './pages/Home';
import { RegisterPortalPage } from './pages/RegisterPortal';
import { SelectClubPage } from './pages/SelectClub';

import { MyBibPage } from './pages/member/MyBib';
import { AchievementsPage } from './pages/member/Achievements';
import { MemberEventsPage } from './pages/member/Events';
import { PaymentsPage } from './pages/member/Payments';
import { MemberVolunteerPage } from './pages/member/Volunteer';
import { LeadEventPage } from './pages/member/LeadEvent';
import { ProfilePage } from './pages/member/Profile';
import { NoticesPage } from './pages/member/Notices';

import { DashboardPage } from './pages/admin/Dashboard';
import { MembersPage } from './pages/admin/Members';
import { MemberDetailPage } from './pages/admin/MemberDetail';
import { DuesPage } from './pages/admin/Dues';
import { AdminEventsPage } from './pages/admin/Events';
import { ResultsPage } from './pages/admin/Results';
import { VolunteeringPage } from './pages/admin/Volunteering';
import { RequestsPage } from './pages/admin/Requests';
import { MessagesPage } from './pages/admin/Messages';
import { SettingsPage } from './pages/admin/Settings';
import { AdminProfilePage } from './pages/admin/Profile';

import {
  SAShell,
  SADashboard,
  SARequests,
  SAPortals,
  SAPortalDetail,
  SAPlans,
  SAPayments,
  SABankDetails,
  SATrace,
  SAEmails,
  SAReports,
} from './pages/sa';
import {
  PortalShell,
  PortalOverview,
  PortalMembers,
  PortalSubscription,
  PortalTrace,
} from './pages/portal';

function CenterNote({ children }) {
  return (
    <div className="mx-auto flex h-[60vh] max-w-sm flex-col items-center justify-center gap-3 px-6 text-center">
      {children}
    </div>
  );
}

/**
 * Blocks anything signed-in-only. A URL can be typed or bookmarked.
 *
 * A signed-out visitor is sent HOME rather than to /signin, and that choice is
 * what makes the browser Back button work (owner's report, 27 Aug 2026).
 * Signing out replaces the current entry with /signin, so every entry BEHIND
 * it is a guarded route. While those guards redirected to /signin as well,
 * pressing Back re-rendered one, which bounced straight forward to /signin
 * again — Back looked broken because the screen never changed. Home is a real
 * destination with its own Sign in button, so the first Back press always
 * gets you somewhere.
 */
function RequireSignIn() {
  const { booting, signedIn } = useAuth();
  if (booting) return <CenterNote><p className="font-market text-xs text-copy-muted">Loading…</p></CenterNote>;
  if (!signedIn) return <Navigate to="/" replace />;
  return <Outlet />;
}

/** The platform console, for the platform owner only. */
function RequireSuperAdmin() {
  const { booting, signedIn, isSuperAdmin } = useAuth();
  if (booting) return <CenterNote><p className="font-market text-xs text-copy-muted">Loading…</p></CenterNote>;
  // Home, not /signin — see RequireSignIn for why Back depends on it.
  if (!signedIn) return <Navigate to="/" replace />;
  if (!isSuperAdmin) return <Navigate to="/select-club" replace />;
  return <Outlet />;
}

/**
 * The club portals need a SELECTED club with its data loaded. No selection →
 * the selector; still loading → the gate (with the sleeping-server note).
 */
function RequireClub() {
  const { activeClubId } = useAuth();
  const { db, loading, waking, loadError, signOut } = useClub();

  if (!activeClubId) return <Navigate to="/select-club" replace />;
  if (loading || (!db && !loadError)) {
    return (
      <CenterNote>
        <p className="font-market text-xs text-copy-muted">Loading your club…</p>
        {waking && (
          <p className="text-sm leading-relaxed text-copy-label">
            Waking the club server after a quiet spell. This one takes up to a
            minute — every load after it is quick.
          </p>
        )}
      </CenterNote>
    );
  }
  if (loadError) {
    return (
      <CenterNote>
        <p className="font-market text-lg tracking-[-0.025em] font-bold text-ink-700">
          Could not open the club
        </p>
        <p className="text-sm text-copy-muted">{loadError}</p>
        {/* Reloading re-selects the SAME club from storage, so on a club that
            keeps failing — suspended mid-session, a 500 — "Try again" was the
            only control and it looped. The other two doors have to be here. */}
        <div className="mt-1 flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="bg-gold-500 px-4 py-2 font-market text-xs uppercase tracking-wide text-ink-800"
          >
            Try again
          </button>
          <Link
            to="/select-club"
            className="border border-line px-4 py-2 font-market text-xs uppercase tracking-wide text-ink-700 hover:border-ink-700"
          >
            Switch club
          </Link>
          <button
            type="button"
            onClick={() => void signOut()}
            className="border border-line px-4 py-2 font-market text-xs uppercase tracking-wide text-copy-muted hover:border-ink-700 hover:text-ink-700"
          >
            Sign out
          </button>
        </div>
      </CenterNote>
    );
  }
  return <Outlet />;
}

/** Committee screens, for committee accounts of the SELECTED club only. */
function RequireAdmin() {
  const { role } = useClub();
  if (role !== 'admin') return <Navigate to="/m/bib" replace />;
  return <Outlet />;
}

/** `/` — the front door sorts everyone to their place. */
function RootGate() {
  const { booting, signedIn, activeClubId } = useAuth();
  const { db, loading } = useClub();
  const { pathname } = useLocation();

  if (booting) {
    return <CenterNote><p className="font-market text-xs text-copy-muted">Loading…</p></CenterNote>;
  }
  if (!signedIn) return <HomePage />;
  if (!activeClubId) return <Navigate to="/select-club" replace />;
  if (loading || !db) {
    return <CenterNote><p className="font-market text-xs text-copy-muted">Loading your club…</p></CenterNote>;
  }
  void pathname;
  return <Navigate to={db.club?.role === 'admin' ? '/a/dashboard' : '/m/bib'} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<RootGate />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/register-portal" element={<RegisterPortalPage />} />

      {/* Forgotten passwords — public by necessity. */}
      <Route path="/forgot" element={<ForgotPasswordPage />} />
      <Route path="/reset/:token" element={<ResetPasswordPage />} />

      {/* One-time links out of a WhatsApp message or an email. */}
      <Route path="/pay/:token" element={<PayPage />} />
      <Route path="/join/:token" element={<JoinPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />

      <Route element={<RequireSignIn />}>
        <Route path="/select-club" element={<SelectClubPage />} />

        {/* portal admin — server-side ownership check per request */}
        <Route path="/p/:portalId" element={<PortalShell />}>
          <Route index element={<PortalOverview />} />
          <Route path="members" element={<PortalMembers />} />
          <Route path="subscription" element={<PortalSubscription />} />
          <Route path="trace" element={<PortalTrace />} />
        </Route>

        {/* the club portals, per selected club */}
        <Route element={<RequireClub />}>
          <Route element={<AppShell />}>
            <Route path="/m/bib" element={<MyBibPage />} />
            <Route path="/m/achievements" element={<AchievementsPage />} />
            <Route path="/m/events" element={<MemberEventsPage />} />
            <Route path="/m/events/:eventId" element={<EventDetailPage />} />
            <Route path="/m/payments" element={<PaymentsPage />} />
            <Route path="/m/volunteer" element={<MemberVolunteerPage />} />
            {/* The event head's desk. Guarded inside the page by canLogForEvent,
                which is also what draws (and removes) its tab in the rail. */}
            <Route path="/m/lead/:eventId" element={<LeadEventPage />} />
            <Route path="/m/profile" element={<ProfilePage />} />
            <Route path="/m/notices" element={<NoticesPage />} />

            <Route element={<RequireAdmin />}>
              <Route path="/a/dashboard" element={<DashboardPage />} />
              <Route path="/a/members" element={<MembersPage />} />
              <Route path="/a/members/:memberId" element={<MemberDetailPage />} />
              <Route path="/a/dues" element={<DuesPage />} />
              <Route path="/a/events" element={<AdminEventsPage />} />
              <Route path="/a/events/:eventId" element={<EventDetailPage />} />
              <Route path="/a/results" element={<ResultsPage />} />
              <Route path="/a/volunteering" element={<VolunteeringPage />} />
              <Route path="/a/requests" element={<RequestsPage />} />
              <Route path="/a/messages" element={<MessagesPage />} />
              <Route path="/a/settings" element={<SettingsPage />} />
              <Route path="/a/profile" element={<AdminProfilePage />} />
            </Route>
          </Route>
        </Route>
      </Route>

      {/* the platform console */}
      <Route element={<RequireSuperAdmin />}>
        <Route element={<SAShell />}>
          <Route path="/sa" element={<Navigate to="/sa/dashboard" replace />} />
          <Route path="/sa/dashboard" element={<SADashboard />} />
          <Route path="/sa/requests" element={<SARequests />} />
          <Route path="/sa/portals" element={<SAPortals />} />
          <Route path="/sa/portals/:portalId" element={<SAPortalDetail />} />
          <Route path="/sa/plans" element={<SAPlans />} />
          <Route path="/sa/payments" element={<SAPayments />} />
          <Route path="/sa/bank" element={<SABankDetails />} />
          <Route path="/sa/trace" element={<SATrace />} />
          <Route path="/sa/emails" element={<SAEmails />} />
          <Route path="/sa/reports" element={<SAReports />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
